import { useCallback, useId, useRef, useState } from "react";
import type {
  ClientSummary,
  KeyContactResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { useDismissed } from "@/components/ui/dismiss";
import { Panel } from "@/components/ui/Panel";

/** What the card names the contact by: the name, or failing that the first way to reach them. */
export function contactLabel(contact: KeyContactResponse): string | null {
  return contact.name ?? contact.email ?? contact.phone ?? contact.job_title;
}

/** A way to reach the contact as a link, or "Not given" where there is none. */
function ContactLink({
  scheme,
  value,
}: {
  scheme: string;
  value: string | null;
}) {
  return value === null ? (
    <span className="text-foreground">Not given</span>
  ) : (
    <a
      href={`${scheme}:${value}`}
      className="text-primary underline underline-offset-2"
    >
      {value}
    </a>
  );
}

function ContactDetails({
  contact,
  onEdit,
}: {
  contact: KeyContactResponse;
  onEdit: () => void;
}) {
  return (
    <>
      <p className="text-[13px] font-bold text-foreground">
        {contact.name ?? "Name not given"}
      </p>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12px]">
        <dt className="text-subtle-foreground">Job title</dt>
        <dd className="text-foreground">{contact.job_title ?? "Not given"}</dd>
        <dt className="text-subtle-foreground">Email</dt>
        <dd className="min-w-0 truncate">
          <ContactLink scheme="mailto" value={contact.email} />
        </dd>
        <dt className="text-subtle-foreground">Contact number</dt>
        <dd>
          <ContactLink scheme="tel" value={contact.phone} />
        </dd>
      </dl>
      <div className="mt-3 flex justify-end">
        <Button variant="secondary" size="dense" onClick={onEdit}>
          Edit
        </Button>
      </div>
    </>
  );
}

/**
 * The key contact on a client's card, opening onto who they are and how to reach
 * them. Their details are changed in the client's own edit dialog, which is
 * where every detail of the client is, so Edit here opens that.
 *
 * A client given no contact says so in the card's words rather than offering
 * a popover with nothing in it.
 */
export function KeyContactPopover({
  client,
  onEdit,
}: {
  client: ClientSummary;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useDismissed(open, panelRef, triggerRef, close);

  const label = contactLabel(client.key_contact);
  if (label === null) return <span>Not given</span>;
  return (
    <span className="relative z-10 inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen((was) => !was);
        }}
        className="cursor-pointer text-primary underline underline-offset-2 hover:text-primary-hover"
      >
        {label}
      </button>
      {open && (
        <Panel
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={`Key contact for ${client.name}`}
          className="absolute top-[calc(100%+6px)] left-0 z-40 w-[300px] not-italic shadow-popover"
        >
          <ContactDetails
            contact={client.key_contact}
            onEdit={() => {
              close();
              onEdit();
            }}
          />
        </Panel>
      )}
    </span>
  );
}
